// shadcn Progress primitive (lightweight, no Radix dep needed). Supports
// both determinate (value 0..100) and indeterminate (value=undefined or 0
// while ``indeterminate`` prop is set) — the indeterminate variant uses a
// CSS animation to slide a 30%-wide bar back and forth, signalling "we're
// working but can't estimate how long" without lying with a fake number.

import { cn } from '@/lib/utils';
import * as React from 'react';

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number; // 0..100
  indeterminate?: boolean;
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value = 0, indeterminate = false, ...props }, ref) => {
    const clamped = Math.min(100, Math.max(0, value));
    return (
      <div
        ref={ref}
        role="progressbar"
        tabIndex={0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : clamped}
        className={cn('relative h-2 w-full overflow-hidden rounded-full bg-secondary', className)}
        {...props}
      >
        {indeterminate ? (
          <div className="absolute inset-y-0 w-1/3 animate-[indeterminate_1.4s_ease-in-out_infinite] rounded-full bg-primary" />
        ) : (
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out"
            style={{ width: `${clamped}%` }}
          />
        )}
      </div>
    );
  },
);
Progress.displayName = 'Progress';

export { Progress };
